/**
 * Pure presentation helpers for the admin notification feed.
 *
 * No React imports — the admin Vitest config runs in a plain node environment
 * (`environment: 'node'`, `include: ['src/**\/*.test.ts']`), so this module has
 * to stay free of JSX and component imports. Icons are referenced by name and
 * resolved to components in the .tsx layer.
 */

import type {
  AdminNotification,
  NotificationCategory,
  NotificationSeverity,
} from '@/lib/api/admin-notifications'

export type NotificationIconName =
  | 'credit-card'
  | 'alert-octagon'
  | 'gift'
  | 'trending-up'
  | 'trending-down'
  | 'message-circle'
  | 'message-square-plus'
  | 'clipboard-check'
  | 'user-plus'
  | 'user-minus'
  | 'layout'
  | 'bell'

export interface NotificationTypeMeta {
  label: string
  category: NotificationCategory
  icon: NotificationIconName
}

/**
 * The catalogue. Kept exhaustive on purpose — the unit test iterates it, so a
 * new emitter type added without metadata fails the build's test step rather
 * than silently rendering a generic bell.
 */
export const NOTIFICATION_TYPES: Record<string, NotificationTypeMeta> = {
  'billing.subscription_started': {
    label: 'New subscription', category: 'billing', icon: 'credit-card',
  },
  'billing.lifetime_purchase': {
    label: 'Lifetime purchase', category: 'billing', icon: 'gift',
  },
  'billing.plan_changed': {
    label: 'Plan changed', category: 'billing', icon: 'trending-up',
  },
  'billing.payment_failed': {
    label: 'Payment failed', category: 'billing', icon: 'alert-octagon',
  },
  'billing.subscription_cancelled': {
    label: 'Subscription cancelled', category: 'billing', icon: 'trending-down',
  },
  'billing.dispute_opened': {
    label: 'Dispute opened', category: 'billing', icon: 'alert-octagon',
  },
  'billing.refund_issued': {
    label: 'Refund issued', category: 'billing', icon: 'credit-card',
  },
  'support.new_conversation': {
    label: 'New support thread', category: 'support', icon: 'message-square-plus',
  },
  'support.new_message': {
    label: 'New support message', category: 'support', icon: 'message-circle',
  },
  'support.qa_feedback': {
    label: 'QA feedback', category: 'support', icon: 'clipboard-check',
  },
  'support.auto_resolved': {
    label: 'Threads auto-resolved', category: 'support', icon: 'clipboard-check',
  },
  'growth.signup': {
    label: 'New signups', category: 'growth', icon: 'user-plus',
  },
  'growth.first_layout': {
    label: 'First layout', category: 'growth', icon: 'layout',
  },
  'growth.account_deleted': {
    label: 'Account deleted', category: 'growth', icon: 'user-minus',
  },
}

const FALLBACK_META: NotificationTypeMeta = {
  label: 'Notification', category: 'growth', icon: 'bell',
}

export function notificationMeta(type: string): NotificationTypeMeta {
  return NOTIFICATION_TYPES[type] ?? FALLBACK_META
}

export const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  billing: 'Billing',
  support: 'Support',
  growth: 'Growth',
}

/**
 * The signup roll-up is stored with a '{count} new signups today' title built in
 * SQL, which reads wrong at exactly one. Pluralize here rather than complicating
 * the trigger.
 */
export function notificationTitle(n: Pick<AdminNotification, 'type' | 'title' | 'metadata'>): string {
  if (n.type === 'growth.signup') {
    const count = Number(n.metadata?.count ?? 1)
    return `${count} new signup${count === 1 ? '' : 's'} today`
  }
  return n.title
}

/**
 * Where clicking the notification should take you. Returns null when the entity
 * no longer exists (a deleted account has nothing to open).
 */
export function notificationHref(
  n: Pick<AdminNotification, 'target_type' | 'target_id'>,
): string | null {
  switch (n.target_type) {
    case 'user':
      return n.target_id ? `/users/${n.target_id}` : '/users'
    case 'conversation':
      return n.target_id ? `/feedback/${n.target_id}` : '/feedback'
    case 'layout':
      return n.target_id ? `/layouts/${n.target_id}` : '/layouts'
    case 'qa_feedback':
      return '/qa-feedback'
    case 'billing':
      return '/billing'
    default:
      return null
  }
}

export function severityIconClass(severity: NotificationSeverity): string {
  switch (severity) {
    case 'critical':
      return 'text-destructive'
    case 'warning':
      return 'text-amber-600'
    case 'success':
      return 'text-emerald-600'
    default:
      return 'text-muted-foreground'
  }
}

/** Badge label. Empty string means "render nothing". */
export function formatBadgeCount(count: number): string {
  if (!Number.isFinite(count) || count <= 0) return ''
  return count > 99 ? '99+' : String(count)
}

export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''

  const seconds = Math.floor((now.getTime() - then) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 7 * 86_400) return `${Math.floor(seconds / 86_400)}d ago`
  return new Date(iso).toLocaleDateString()
}

export interface NotificationGroup {
  label: string
  items: AdminNotification[]
}

/** Groups a feed into Today / Yesterday / Earlier, preserving input order. */
export function groupByDay(
  notifications: AdminNotification[],
  now: Date = new Date(),
): NotificationGroup[] {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const startOfYesterday = startOfToday - 86_400_000

  const buckets: NotificationGroup[] = [
    { label: 'Today', items: [] },
    { label: 'Yesterday', items: [] },
    { label: 'Earlier', items: [] },
  ]

  for (const n of notifications) {
    const at = new Date(n.event_at).getTime()
    if (at >= startOfToday) buckets[0].items.push(n)
    else if (at >= startOfYesterday) buckets[1].items.push(n)
    else buckets[2].items.push(n)
  }

  return buckets.filter((b) => b.items.length > 0)
}
